import { useCallback, useMemo, useReducer } from 'react';
import { Calendar, dateFnsLocalizer, type View, type EventProps } from 'react-big-calendar';
import {
  format as dateFnsFormat,
  parse,
  startOfWeek,
  startOfMonth,
  startOfDay,
  endOfWeek,
  endOfMonth,
  endOfDay,
  addDays,
  getDay,
} from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';

import { useAuth } from '@/features/auth';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { EventDialog } from '../components/EventDialog';
import { EventDetailsDialog } from '../components/EventDetailsDialog';
import { DeleteEventDialog } from '../components/DeleteEventDialog';
import { CalendarFilterBar } from '../components/CalendarFilterBar';
import { useEventMutations, useEvent, useCalendarFilters } from '../hooks';
import { fetchEvents } from '../api';
import {
  RBC_MESSAGES,
  WEEKDAY_SHORT,
  EVENT_STYLES,
  EVENT_COLORS,
  type BigCalendarEvent,
} from '../constants';
import type { CalendarEvent } from '../types';

/* -------------------- Custom Event Component -------------------- */
function CustomEvent({ event }: EventProps<BigCalendarEvent>) {
  const target = event.target || 'CASE_MEETING';
  const style = EVENT_STYLES[target as keyof typeof EVENT_STYLES] || EVENT_STYLES.CASE_MEETING;

  return (
    <div className={cn('h-full w-full', style.className)}>
      <div className="truncate font-medium text-xs">
        {event.description || event.title}
      </div>
      {(event.matterName || event.clientName) && (
        <div className="truncate text-[10px] opacity-70 mt-0.5">
          {event.matterName} {event.clientName && `· ${event.clientName}`}
        </div>
      )}
    </div>
  );
}

interface DialogState {
  eventDialogOpen: boolean;
  detailsDialogOpen: boolean;
  deleteDialogOpen: boolean;
  selectedEventId: number | undefined;
  slotInfo: { start: Date; end: Date } | null;
}

type DialogAction =
  | { type: 'OPEN_CREATE'; slotInfo: { start: Date; end: Date } }
  | { type: 'OPEN_EDIT'; eventId: number }
  | { type: 'OPEN_DETAILS'; eventId: number }
  | { type: 'OPEN_DELETE' }
  | { type: 'CLOSE_EVENT_DIALOG' }
  | { type: 'CLOSE_DETAILS_DIALOG' }
  | { type: 'CLOSE_DELETE_DIALOG' }
  | { type: 'RESET_SELECTION' };

const INITIAL_DIALOG_STATE: DialogState = {
  eventDialogOpen: false,
  detailsDialogOpen: false,
  deleteDialogOpen: false,
  selectedEventId: undefined,
  slotInfo: null,
};

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
  switch (action.type) {
    case 'OPEN_CREATE':
      return { ...state, eventDialogOpen: true, selectedEventId: undefined, slotInfo: action.slotInfo };
    case 'OPEN_EDIT':
      return { ...state, detailsDialogOpen: false, eventDialogOpen: true };
    case 'OPEN_DETAILS':
      return { ...state, detailsDialogOpen: true, selectedEventId: action.eventId, slotInfo: null };
    case 'OPEN_DELETE':
      return { ...state, detailsDialogOpen: false, deleteDialogOpen: true };
    case 'CLOSE_EVENT_DIALOG':
      return { ...state, eventDialogOpen: false };
    case 'CLOSE_DETAILS_DIALOG':
      return { ...state, detailsDialogOpen: false };
    case 'CLOSE_DELETE_DIALOG':
      return { ...state, deleteDialogOpen: false, selectedEventId: undefined };
    case 'RESET_SELECTION':
      return { ...state, selectedEventId: undefined, slotInfo: null };
  }
}

interface ApiFilters {
  'date.min'?: Date;
  'date.max'?: Date;
  createdById?: number;
  attendeeId?: number;
  clientId?: number;
  matterId?: number;
  target?: string;
}

export function CalendarPage() {
  const { t, i18n } = useTranslation();
  const { hasAccess } = useAuth();
  const {
    filters: urlFilters,
    updateFilter,
    updateFilters,
    resetFilters,
    currentView,
    currentDate,
    setView,
    setDate,
  } = useCalendarFilters();

  const lang = i18n.language?.startsWith('ru') ? 'ru' : 'en';
  const locales = useMemo(() => ({ en: enUS, ru }), []);

  const localizer = useMemo(
    () =>
      dateFnsLocalizer({
        format: dateFnsFormat,
        parse,
        startOfWeek: (date: Date) => startOfWeek(date, { locale: locales[lang] }),
        getDay,
        locales,
      }),
    [lang, locales]
  );

  // Dialog states
  const [dialogState, dispatchDialog] = useReducer(dialogReducer, INITIAL_DIALOG_STATE);
  const { eventDialogOpen, detailsDialogOpen, deleteDialogOpen, selectedEventId, slotInfo } = dialogState;

  // Fetch data
  const { data: selectedEvent } = useEvent(selectedEventId);
  const { deleteMutation } = useEventMutations();

  /* -------------------- Date range into filters -------------------- */
  const getCurrentPeriod = useCallback((date: Date, view: View) => {
    if (view === 'agenda') {
      return { start: startOfDay(date), end: endOfDay(addDays(date, 30)) };
    }

    const startFns: Record<string, (d: Date) => Date> = {
      month: startOfMonth,
      week: (d: Date) => startOfWeek(d, { locale: locales[lang] }),
      day: startOfDay,
    };
    const endFns: Record<string, (d: Date) => Date> = {
      month: endOfMonth,
      week: (d: Date) => endOfWeek(d, { locale: locales[lang] }),
      day: endOfDay,
    };

    const startFn = startFns[view] ?? startOfMonth;
    const endFn = endFns[view] ?? endOfMonth;
    return { start: startFn(date), end: endFn(date) };
  }, [lang, locales]);

  // Compute API filters from URL filters and current date/view
  const apiFilters = useMemo<ApiFilters>(() => {
    const { start, end } = getCurrentPeriod(currentDate, currentView);
    return {
      'date.min': start,
      'date.max': end,
      createdById: urlFilters.createdById,
      attendeeId: urlFilters.attendeeId,
      clientId: urlFilters.clientId,
      matterId: urlFilters.matterId,
      target: urlFilters.target,
    };
  }, [currentDate, currentView, urlFilters, getCurrentPeriod]);

  /* -------------------- Fetch events -------------------- */
  const { data: events } = useQuery({
    queryKey: ['getAllEvents', apiFilters],
    queryFn: () => fetchEvents(apiFilters),
  });

  /* -------------------- Handlers -------------------- */
  const handleViewChange = useCallback((newView: View) => setView(newView), [setView]);
  const handleNavigate = useCallback((date: Date) => setDate(date), [setDate]);

  const handleSelectSlot = useCallback(
    ({ start, end }: { start: Date; end: Date }) => {
      // In month view a day click drills into that day's events (Day view)
      // instead of opening the create dialog. Creating is done from the
      // header "Add event" button or by selecting a slot in day/week view.
      // Set date + view in one update so neither param clobbers the other.
      if (currentView === 'month') {
        updateFilters({ date: dateFnsFormat(start, 'yyyy-MM-dd'), view: 'day' });
        return;
      }
      if (hasAccess('event', 'create')) {
        dispatchDialog({ type: 'OPEN_CREATE', slotInfo: { start: new Date(start), end: new Date(end) } });
      }
    },
    [currentView, updateFilters, hasAccess]
  );

  // Clicking the date number (month/week drill-down) → that day's Day view.
  // Atomic update — separate setDate + setView calls clobber each other.
  const handleDrillDown = useCallback(
    (date: Date) => {
      updateFilters({ date: dateFnsFormat(date, 'yyyy-MM-dd'), view: 'day' });
    },
    [updateFilters]
  );

  const handleSelectEvent = useCallback((event: BigCalendarEvent) => {
    dispatchDialog({ type: 'OPEN_DETAILS', eventId: event.id });
  }, []);

  const handleEditEvent = useCallback(() => {
    dispatchDialog({ type: 'OPEN_EDIT', eventId: selectedEventId! });
  }, [selectedEventId]);

  const handleDeleteClick = useCallback(() => {
    dispatchDialog({ type: 'OPEN_DELETE' });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (selectedEventId) {
      await deleteMutation.mutateAsync(selectedEventId);
      dispatchDialog({ type: 'CLOSE_DELETE_DIALOG' });
    }
  }, [selectedEventId, deleteMutation]);

  const handleEventDialogSuccess = useCallback(() => {
    dispatchDialog({ type: 'RESET_SELECTION' });
  }, []);

  /* -------------------- Map events to calendar format -------------------- */
  const calendarEvents: BigCalendarEvent[] = useMemo(
    () =>
      (events?.results ?? [])
        .filter((item: CalendarEvent) => item?.startsAt && item?.endsAt)
        .map((item: CalendarEvent) => ({
          id: item.id,
          title: item.description || '',
          description: item.description || '',
          target: item.target,
          matterName: item.matter?.name,
          clientName: item.matter?.client?.name || item.client?.name,
          color:
            item?.target === 'FIRM_EVENT'
              ? '#059669'
              : item?.target === 'NEW_CLIENT'
                ? '#0369a1'
                : '#7c3aed',
          start: new Date(item.startsAt),
          end: new Date(item.endsAt),
        })),
    [events]
  );

  /* -------------------- Event style -------------------- */
  const eventStyleGetter = useCallback((event: BigCalendarEvent) => {
    const target = event.target || 'CASE_MEETING';
    const color = EVENT_COLORS[target as keyof typeof EVENT_COLORS] || EVENT_COLORS.CASE_MEETING;

    return {
      style: {
        backgroundColor: color.bg,
        color: color.text,
        borderLeft: `3px solid ${color.border}`,
        borderRadius: '4px',
        border: 'none',
        padding: '4px 8px',
        fontSize: '12px',
        fontWeight: 500,
      },
    };
  }, []);

  /* -------------------- Force weekday labels -------------------- */
  const formats = useMemo(
    () => ({
      weekdayFormat: (date: Date) => {
        const idx = getDay(date);
        return WEEKDAY_SHORT[lang][idx];
      },
      dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
        `${dateFnsFormat(start, 'd MMM', { locale: locales[lang] })} – ${dateFnsFormat(end, 'd MMM', { locale: locales[lang] })}`,
    }),
    [lang, locales]
  );

  /* -------------------- Custom Components -------------------- */
  const components = useMemo(
    () => ({
      event: CustomEvent,
    }),
    []
  );

  return (
    <div className="flex flex-col h-full bg-[#f8fafc]">
      {/* Page Header */}
      <div className="flex py-4 px-6 justify-between items-center bg-white border-b border-[#e2e8f0]">
        <h1 className="text-2xl font-semibold text-[#1a2b4a]">{t('calendar')}</h1>
        <div className="flex items-center gap-3">
          {hasAccess('event', 'create') && (
            <Button
              onClick={() => {
                dispatchDialog({ type: 'OPEN_CREATE', slotInfo: { start: new Date(), end: new Date() } });
              }}
              className="bg-[#1a2b4a] hover:bg-[#0f1a2e] text-white gap-2"
            >
              <Plus className="h-4 w-4" />
              {t('addEvent')}
            </Button>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <CalendarFilterBar
        filters={urlFilters}
        updateFilter={updateFilter}
        resetFilters={resetFilters}
      />

      {/* Event Type Legend */}
      <div className="flex items-center gap-6 px-6 py-3 bg-white border-b border-[#e2e8f0]">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-[#d1fae5] border-l-[3px] border-[#10b981]" />
          <span className="text-xs text-[#64748b]">{t('target.FIRM_EVENT')}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-[#e0f2fe] border-l-[3px] border-[#0284c7]" />
          <span className="text-xs text-[#64748b]">{t('target.NEW_CLIENT')}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-[#f5f3ff] border-l-[3px] border-[#8b5cf6]" />
          <span className="text-xs text-[#64748b]">{t('target.CASE_MEETING')}</span>
        </div>
      </div>

      {/* Calendar */}
      <div className="flex-1 p-6">
        <Calendar
          localizer={localizer}
          culture={lang}
          messages={RBC_MESSAGES[lang]}
          formats={formats}
          events={calendarEvents}
          date={currentDate}
          view={currentView}
          views={['agenda', 'day', 'month', 'week']}
          selectable
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%', minHeight: '600px' }}
          onView={handleViewChange}
          onNavigate={handleNavigate}
          onDrillDown={handleDrillDown}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          eventPropGetter={eventStyleGetter}
          components={components}
          length={30}
        />
      </div>

      {/* Event Create/Edit Dialog */}
      <EventDialog
        open={eventDialogOpen}
        onOpenChange={(open) => { if (!open) dispatchDialog({ type: 'CLOSE_EVENT_DIALOG' }); }}
        event={selectedEvent as CalendarEvent | null | undefined}
        defaultDate={slotInfo?.start}
        onSuccess={handleEventDialogSuccess}
      />

      {/* Event Details Dialog */}
      <EventDetailsDialog
        open={detailsDialogOpen}
        onOpenChange={(open) => { if (!open) dispatchDialog({ type: 'CLOSE_DETAILS_DIALOG' }); }}
        event={selectedEvent ?? null}
        onEdit={hasAccess('event', 'update') ? handleEditEvent : undefined}
        onDelete={hasAccess('event', 'delete') ? handleDeleteClick : undefined}
        canEdit={hasAccess('event', 'update')}
        canDelete={hasAccess('event', 'delete')}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteEventDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => { if (!open) dispatchDialog({ type: 'CLOSE_DELETE_DIALOG' }); }}
        event={selectedEvent ?? null}
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
